import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

type HlsPlayerProps = {
  src: string;
  autoPlay?: boolean;
  muted?: boolean;
  className?: string;
  style?: React.CSSProperties;
  initialSize?: { width: number; height: number };
  onSizeChange?: (size: { width: number; height: number }) => void;
  resizable?: boolean; // Enable resize functionality
  maximizable?: boolean; // Enable maximize functionality
};

export default function HlsPlayer({
  src,
  autoPlay = true,
  muted = true,
  className = "",
  style = {},
  initialSize,
  onSizeChange,
  resizable = true,
  maximizable = true,
}: HlsPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    initialSize || null
  );
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  // If no initial size, use container size (fill parent)
  const useContainerSize = !initialSize;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls: Hls | null = null;

    // Chrome / Firefox
    if (Hls.isSupported()) {
      hls = new Hls({
        lowLatencyMode: true,
      });
      hls.loadSource(src);
      hls.attachMedia(video);
    }
    // Safari (native HLS)
    else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = src;
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [src]);

  // Handle resize mouse events
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !size) return;
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;
      const newWidth = Math.max(200, resizeStart.width + deltaX);
      const newHeight = Math.max(150, resizeStart.height + deltaY);
      const newSize = { width: newWidth, height: newHeight };
      setSize(newSize);
      onSizeChange?.(newSize);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, resizeStart, onSizeChange, size]);

  const handleResizeStart = (e: React.MouseEvent) => {
    if (isMaximized || !resizable || !size) return;
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    });
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // If maximized, render fullscreen version
  if (isMaximized && maximizable) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
        <div className="relative w-full h-full flex flex-col">
          <div className="absolute top-4 right-4 z-10 flex gap-2">
            <button
              onClick={toggleMaximize}
              className="bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              title="Minimize"
            >
              <i className="fa-solid fa-compress"></i>
              Minimize
            </button>
          </div>
          <video
            ref={videoRef}
            controls
            autoPlay={autoPlay}
            muted={muted}
            playsInline
            className="w-full h-full object-contain"
            style={{ backgroundColor: "black" }}
          />
        </div>
      </div>
    );
  }

  // Normal container - either fixed size or fill parent
  const containerStyle = useContainerSize
    ? { width: "100%", height: "100%", ...style }
    : {
        width: `${size?.width}px`,
        height: `${size?.height}px`,
        ...style,
      };

  return (
    <div
      ref={containerRef}
      className={`relative bg-black ${useContainerSize ? "" : "rounded-lg overflow-hidden shadow-lg border border-gray-700"} ${className}`}
      style={containerStyle}
    >
      <video
        ref={videoRef}
        controls
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        className="w-full h-full object-contain"
        style={{ backgroundColor: "black" }}
      />

      {/* Controls Overlay - only show if resizable or maximizable */}
      {(resizable || maximizable) && (
        <div className="absolute top-2 right-2 flex gap-2 opacity-0 hover:opacity-100 transition-opacity z-10">
          {maximizable && (
            <button
              onClick={toggleMaximize}
              className="bg-gray-800/80 hover:bg-gray-700/80 text-white p-2 rounded transition-colors"
              title="Maximize"
            >
              <i className="fa-solid fa-expand text-xs"></i>
            </button>
          )}
        </div>
      )}

      {/* Resize Handle - only show if resizable and has fixed size */}
      {resizable && !useContainerSize && size && (
        <div
          onMouseDown={handleResizeStart}
          className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize bg-gray-600/50 hover:bg-gray-500/50 transition-colors"
          style={{
            clipPath: "polygon(100% 0, 0 100%, 100% 100%)",
          }}
          title="Drag to resize"
        />
      )}
    </div>
  );
}
